import { useTranslation } from 'react-i18next'

import { RejectDialog } from '~/components/bidding/common/RejectDialog'
import { SubmitConfirmModal } from '~/components/bidding/common/SubmitConfirmModal'
import type { IntradayApprovalDecision } from '~/pages/IntradayBidding/hooks/useIntradayApprovalDecision'
import type { ApprovalStageProgress } from '~/pages/IntradayBidding/models/approvalStageProgress'

type Props = {
  /** 承認 / 差戻しの対象・確定・違反と警告 (`useIntradayApprovalDecision`)。 */
  decision: IntradayApprovalDecision
  /**
   * 承認待ちの札が待っている段の進捗 (多段階承認、 2 段以上の時だけ)。 渡された時だけ承認確認に
   * 「{N}/{M}段階」と承認後の遷移を 1 行出す。 値を持たない呼び出し元 (一覧の行) は渡さない。
   */
  stageProgress?: ApprovalStageProgress
}

/**
 * 時間前入札の承認確認モーダル (AC-BIN-097) と差戻しダイアログ (AC-BIN-053: 理由必須) の
 * 組み立てを 1 か所に閉じる。 入札一覧の操作列 (`BidCreateTab`) と詳細モーダル
 * (`IntradayOrderDetailModal`) が同じ文言 ・ 同じ構成を使うので、 AC-BIN-097 / 098 の
 * 注記や違反セクションの構成を変えるときに JSX を 2 か所で同期更新しなくてよい。
 *
 * 呼び出し元は「開く契機」 だけを持つ。 確定時の遷移 ・ 違反明細 ・ 警告の中身は
 * `useIntradayApprovalDecision` が組み立てる。
 *
 * - 承認確認は申請内容サマリを 1 行の明細で提示してから確定する (エリア別内訳 ・
 *   数量合計はスポットのバッチ承認向けで、 1 モーダル 1 札の時間前では同じ数字の重複に
 *   なるため渡さない)。
 * - テナント入札制限 (AC-BIN-015) は事前取得していないため、 「承認する」 を押した APPROVE
 *   RPC が違反で失敗して初めて分かる。 違反が判明したらモーダルは閉じずに明細を
 *   インライン表示し、 確定ボタンを非活性にする (承認者は札を直せないので 「差戻し」 で
 *   申請者に戻す)。
 */
export const IntradayApprovalDialogs = ({
  decision,
  stageProgress,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const stageLine =
    stageProgress === undefined
      ? undefined
      : t('page:biddingIntraday.approvalAction.approvalStageProgress', {
          current: stageProgress.current,
          total: stageProgress.total,
        })
  const stageBody =
    stageProgress === undefined
      ? null
      : stageProgress.current < stageProgress.total
        ? t('page:biddingIntraday.approvalAction.approvalStageConfirmNext', {
            next: stageProgress.current + 1,
          })
        : t('page:biddingIntraday.approvalAction.approvalStageConfirmFinal')
  return (
    <>
      <SubmitConfirmModal
        bidCount={1}
        body={stageBody}
        confirmLabel={t('page:biddingIntraday.approvalAction.approveConfirm')}
        detailLines={
          stageLine === undefined
            ? [decision.approvalDetailLine]
            : [stageLine, decision.approvalDetailLine]
        }
        hasViolation={decision.hasApproveViolation}
        isOpen={decision.approvingOrder !== undefined}
        isSubmitting={decision.isTransitioning}
        marketLabel={t('page:biddingIntraday.market.label')}
        onClose={decision.closeApprove}
        onConfirm={() => decision.approve()}
        submittingLabel={t('page:biddingIntraday.approvalAction.approving')}
        title={t('page:biddingIntraday.approvalAction.approveModalTitle')}
        violationSections={decision.approveViolationSections}
        warningSections={decision.approvalWarningSections}
      />
      <RejectDialog
        description={decision.rejectDescription}
        isLoading={decision.isTransitioning}
        isOpen={decision.rejectingOrder !== undefined}
        onClose={decision.closeReject}
        onConfirm={(reason) => {
          void decision.reject(reason)
        }}
        title={t('page:biddingIntraday.approvalAction.rejectDialogTitle')}
      />
    </>
  )
}
